import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { updateConfirmationDialogDarkMode } from "../utilities/createConfirmationDialog";
import { updateMarkdownDialogDarkMode } from "../utilities/createMarkdownDialog";
import { brandPrefersDark } from "../theme";
import { getActiveBrand } from "../branding/activeBrand";

interface DarkModeContextType {
  darkMode: boolean;
  setDarkMode: (isDark: boolean) => void;
}

export const DarkModeContext = createContext<DarkModeContextType>({
  darkMode: false,
  setDarkMode: () => {},
});

export const useDarkMode = () => useContext(DarkModeContext);

interface DarkModeProviderProps {
  children: ReactNode;
}

export const DarkModeProvider = ({ children }: DarkModeProviderProps) => {
  const [darkMode, setDarkModeState] = useState(() => {
    // A fresh key ("v2"): the removed-toggle era auto-persisted darkMode=false for everyone,
    // which would otherwise override the dark default here. Honour a real saved choice under
    // the new key; otherwise default to the brand's preference (CerebraLinked = dark).
    const saved = localStorage.getItem("darkMode.v2");
    return saved != null ? JSON.parse(saved) : brandPrefersDark(getActiveBrand().key);
  });
  // Persist the dark-mode choice to localStorage whenever it changes. There is no server-side
  // preferences endpoint — the preference is intentionally device-local.
  useEffect(() => {
    localStorage.setItem("darkMode.v2", JSON.stringify(darkMode));
    updateConfirmationDialogDarkMode(darkMode);
    updateMarkdownDialogDarkMode(darkMode);
  }, [darkMode]);

  const setDarkMode = (isDark: boolean) => {
    setDarkModeState(isDark);
  };

  return (
    <DarkModeContext.Provider value={{ darkMode, setDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};
